import { Injectable, inject } from '@angular/core';
import { forkJoin, Observable, of, map, catchError, switchMap } from 'rxjs';
import {
  BulkPaymentRow,
  BulkPaymentMatch,
  BulkPaymentResult,
  BulkPaymentCsvData,
  BulkPaymentSummary,
} from '../models/bulk-payment.model';
import { CustomerModel, PaymentMethodModel } from '../../customer/customer.model';
import { TransactionsService } from '../../transactions/services/transactions.service';
import { CustomersService } from '../../customer/services/customers.service';
import { TransactionSalePayload } from '../../transactions/transaction.model';

@Injectable({
  providedIn: 'root',
})
export class BulkPaymentService {
  private transactionsService = inject(TransactionsService);
  private customersService = inject(CustomersService);

  /**
   * Parse CSV data into bulk payment rows
   */
  parseCsvData(csvData: BulkPaymentCsvData[]): BulkPaymentRow[] {
    return csvData.map((row, index) => {
      const rawAmount = row['Amount']?.toString().trim() || '';
      const stripped = rawAmount.replace(/[^0-9.\-]/g, '');
      const isNumericShape = /^\d+(\.\d+)?$/.test(stripped);
      const n = parseFloat(stripped);
      const isAmountValid = isNumericShape && !isNaN(n) && n > 0;

      const rawInvoiceNumber = row['Invoice Number']?.toString() ?? '';
      const trimmedInvoice = rawInvoiceNumber.trim();
      const isInvoiceValid = trimmedInvoice.length > 0;

      return {
        accountNumber: row['Account Number']?.trim() || '',
        amount: isAmountValid ? n : 0,
        rawAmount,
        isAmountValid,
        invoiceNumber: isInvoiceValid ? trimmedInvoice : undefined,
        rawInvoiceNumber,
        isInvoiceValid,
        rowIndex: index + 1,
      };
    });
  }

  /**
   * Match CSV rows with existing customers
   */
  matchCustomers(rows: BulkPaymentRow[], customers: CustomerModel[]): Observable<BulkPaymentMatch[]> {
    // Handle empty rows case
    if (rows.length === 0) {
      return of([]);
    }

    const matches: BulkPaymentMatch[] = rows.map((row) => {
      // Short-circuit rows with no account number
      if (!row.accountNumber) {
        return {
          row,
          status: 'no-customer' as const,
          error: 'Missing account number',
        };
      }

      // Find customer by companyAccountNumber
      const customer = customers.find((c) => c.companyAccountNumber?.toLowerCase() === row.accountNumber.toLowerCase());

      if (!customer) {
        return {
          row,
          status: 'no-customer' as const,
          error: 'Not Found',
        };
      }

      return {
        row,
        customer,
        status: 'matched' as const,
      };
    });

    // For matched customers, fetch their payment methods
    const matchObservables = matches.map((match) => {
      if (match.status === 'matched' && match.customer?.id) {
        return this.getDefaultPaymentMethod(match.customer.id).pipe(
          map((paymentMethod) => ({
            ...match,
            paymentMethod,
            status: paymentMethod ? ('matched' as const) : ('no-payment-method' as const),
            error: paymentMethod
              ? undefined
              : `No default payment method found for customer "${match.customer?.displayName}"`,
          })),
          catchError((error) => {
            console.error(`Error getting payment method for customer ${match.customer?.id}:`, error);
            return of({
              ...match,
              status: 'no-payment-method' as const,
              error: `Failed to load payment method: ${error.message || 'Unknown error'}`,
            });
          })
        );
      }
      return of(match);
    });

    // Handle empty observables array
    if (matchObservables.length === 0) {
      return of([]);
    }

    return forkJoin(matchObservables);
  }

  /**
   * Get default payment method for a customer
   */
  private getDefaultPaymentMethod(customerId: string): Observable<PaymentMethodModel | undefined> {
    // Fetch card payment methods
    const cardMethods$ = this.customersService.getPaymentMethods(customerId).pipe(
      catchError((error) => {
        console.error(`Error fetching card payment methods for customer ${customerId}:`, error);
        return of([]);
      })
    );

    // Fetch ACH payment methods
    const achMethods$ = this.customersService.getAchPaymentMethods(customerId).pipe(
      catchError((error) => {
        console.error(`Error fetching ACH payment methods for customer ${customerId}:`, error);
        return of([]);
      })
    );

    return forkJoin({
      cardMethods: cardMethods$,
      achMethods: achMethods$,
    }).pipe(
      map(({ cardMethods, achMethods }) => {
        // Combine all payment methods
        const allMethods: PaymentMethodModel[] = [...cardMethods, ...achMethods];

        // Find the default payment method
        const defaultMethod = allMethods.find((method) => method.isDefault === true);

        // If no default, return the first available method
        return defaultMethod || allMethods[0];
      }),
      catchError((error) => {
        console.error(`Error in getDefaultPaymentMethod for customer ${customerId}:`, error);
        return of(undefined);
      })
    );
  }

  /**
   * Process bulk payments
   */
  processBulkPayments(matches: BulkPaymentMatch[]): Observable<BulkPaymentResult[]> {
    // Filter only fully ready payments: matched customer + payment method + valid amount + valid invoice
    const validMatches = matches.filter(
      (match) =>
        match.status === 'matched' &&
        match.customer &&
        match.paymentMethod &&
        match.row.isAmountValid &&
        match.row.isInvoiceValid
    );

    if (validMatches.length === 0) {
      return of([]);
    }

    // Create payment observables for each valid match
    const paymentObservables = validMatches.map((match) =>
      this.processSinglePayment(match).pipe(
        catchError((error) => {
          const decline = error?.data?.transactionResponse;
          const reason =
            decline?.verbiage ||
            error?.data?.message ||
            'The payment was not approved by the processor.';
          return of({
            row: match.row,
            customer: match.customer,
            status: 'failed' as const,
            transactionId: decline?.transactionId || undefined,
            error: reason,
            responseMessage: decline?.hardCode || undefined,
          });
        })
      )
    );

    // Process all payments in parallel
    return forkJoin(paymentObservables);
  }

  /**
   * Process a single payment
   */
  private processSinglePayment(match: BulkPaymentMatch): Observable<BulkPaymentResult> {
    // `reference` is guaranteed non-empty here: processBulkPayments filters on isInvoiceValid.
    // The server's `SimpleSaleRequest` Joi schema rejects an empty `reference` with 400.
    const payload: TransactionSalePayload = {
      customerId: match.customer!.id,
      reference: match.row.invoiceNumber ?? '',
      amount: match.row.amount,
      taxAmount: 0,
      shippingAmount: 0,
      costOfCapitalAmount: match.row.costOfCapitalAmount || 0,
      tokenId: match.paymentMethod!.id,
    };

    return this.transactionsService.transactionSale(payload).pipe(
      map((transaction) => ({
        row: match.row,
        customer: match.customer,
        status: 'success' as const,
        transactionId: transaction.transactionId,
        responseMessage: transaction.resultCodeVerbaige,
      }))
    );
  }

  /**
   * Calculate summary statistics
   */
  calculateSummary(matches: BulkPaymentMatch[], results: BulkPaymentResult[]): BulkPaymentSummary {
    return {
      total: matches.length,
      matched: matches.filter((m) => m.status === 'matched').length,
      unmatched: matches.filter((m) => m.status !== 'matched').length,
      success: results.filter((r) => r.status === 'success').length,
      failed: results.filter((r) => r.status === 'failed').length,
      pending: results.filter((r) => r.status === 'pending').length,
    };
  }

  /**
   * Generate CSV content from results
   */
  generateResultsCsv(matches: BulkPaymentMatch[], results: BulkPaymentResult[]): string {
    // Check if any match has cost of capital
    const hasCostOfCapital = matches.some((m) => (m.row.costOfCapitalAmount || 0) > 0);

    const headers = hasCostOfCapital
      ? [
          'Row',
          'Account Number',
          'Amount',
          'Cost of Capital',
          'Total',
          'Invoice Number',
          'Status',
          'Transaction ID',
          'Error/Message',
        ]
      : ['Row', 'Account Number', 'Amount', 'Invoice Number', 'Status', 'Transaction ID', 'Error/Message'];

    let csvContent = headers.join(',') + '\n';

    // Create a map of results by row index for quick lookup
    const resultsMap = new Map(results.map((r) => [r.row.rowIndex, r]));

    matches.forEach((match) => {
      const result = resultsMap.get(match.row.rowIndex);
      const status = result
        ? result.status === 'success'
          ? 'Approved'
          : result.status === 'failed'
            ? 'Declined'
            : 'Pending'
        : 'Skipped';
      const transactionId = result?.transactionId || '';
      const message = result?.error || result?.responseMessage || match.error || '';
      const costOfCapital = match.row.costOfCapitalAmount || 0;
      const total = match.row.amount + costOfCapital;

      const row = hasCostOfCapital
        ? [
            match.row.rowIndex,
            `"${match.row.accountNumber}"`,
            match.row.amount.toFixed(2),
            costOfCapital.toFixed(2),
            total.toFixed(2),
            `"${match.row.invoiceNumber || ''}"`,
            status,
            transactionId,
            `"${message}"`,
          ].join(',')
        : [
            match.row.rowIndex,
            `"${match.row.accountNumber}"`,
            match.row.amount.toFixed(2),
            `"${match.row.invoiceNumber || ''}"`,
            status,
            transactionId,
            `"${message}"`,
          ].join(',');

      csvContent += row + '\n';
    });

    return csvContent;
  }

  /**
   * Download CSV file
   */
  downloadCsv(content: string, filename: string = 'bulk-payment-results.csv'): void {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }
}
